import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveTable, ResponsiveTableColumn } from '@/components/common/ResponsiveTable';
import { Badge } from '@/components/common/Badge';
import { Alert } from '@/components/common/Alert';
import { apiClient } from '@/services/api/client';
import { productService } from '@/services/product.service';
import { XMarkIcon } from '@heroicons/react/24/outline';
import '@/styles/dashboard.css';

interface Avis {
  id: number;
  customer_name: string;
  customer_email?: string;
  rating?: number;
  comment: string;
  product_id?: number;
  product_name?: string;
  status?: 'pending' | 'validated' | 'rejected';
  featured_on_home?: boolean;
  created_at: string;
  updated_at?: string;
}

const ITEMS_PER_PAGE = 10;

export const AvisProduits = () => {
  const { t } = useTranslation();
  const [avis, setAvis] = useState<Avis[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filtres
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [ratingFilter, setRatingFilter] = useState<string>('all');
  const [productFilter, setProductFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [detailModalOpen, setDetailModalOpen] = useState<Avis | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<number | null>(null);

  // Charger les données
  useEffect(() => {
    loadAvis();
    loadProducts();
  }, []);

  const loadAvis = async () => {
    try {
      setLoading(true);
      const data = await apiClient.get<Avis[]>('/cms/testimonials');
      // Enrichir avec les noms de produits
      const enrichedAvis = await Promise.all(
        data.map(async (avi) => {
          if (avi.product_id) {
            try {
              const product = await productService.getById(avi.product_id);
              return { ...avi, product_name: product.name };
            } catch (e) {
              return avi;
            }
          }
          return avi;
        })
      );
      setAvis(enrichedAvis);
      setError(null);
    } catch (err: any) {
      console.error('Erreur lors du chargement des avis:', err);
      setError(t('adminReviews.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const data = await productService.getAll();
      setProducts(data);
    } catch (err) {
      console.error('Erreur lors du chargement des produits:', err);
    }
  };

  const filteredAvis = useMemo(() => {
    let filtered = [...avis];

    // Recherche par nom, email ou commentaire
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(avi =>
        avi.customer_name?.toLowerCase().includes(query) ||
        avi.customer_email?.toLowerCase().includes(query) ||
        avi.comment?.toLowerCase().includes(query) ||
        avi.product_name?.toLowerCase().includes(query)
      );
    }

    // Filtre par statut
    if (statusFilter !== 'all') {
      filtered = filtered.filter(avi => avi.status === statusFilter);
    }

    // Filtre par note
    if (ratingFilter !== 'all') {
      const rating = parseInt(ratingFilter);
      filtered = filtered.filter(avi => avi.rating === rating);
    }

    // Filtre par produit
    if (productFilter !== 'all') {
      const productId = parseInt(productFilter);
      filtered = filtered.filter(avi => avi.product_id === productId);
    }

    // Filtre par date de début
    if (dateFrom) {
      filtered = filtered.filter(avi => {
        const aviDate = new Date(avi.created_at).toISOString().split('T')[0];
        return aviDate >= dateFrom;
      });
    }

    // Filtre par date de fin
    if (dateTo) {
      filtered = filtered.filter(avi => {
        const aviDate = new Date(avi.created_at).toISOString().split('T')[0];
        return aviDate <= dateTo;
      });
    }

    return filtered;
  }, [avis, searchQuery, statusFilter, ratingFilter, productFilter, dateFrom, dateTo]);

  const totalPages = Math.ceil(filteredAvis.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedAvis = filteredAvis.slice(startIndex, endIndex);

  // Réinitialiser la page quand les filtres changent
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, ratingFilter, productFilter, dateFrom, dateTo]);

  const getStatusBadge = (status?: string) => {
    const statusMap: Record<string, { text: string; variant: 'success' | 'error' | 'warning' | 'info' | 'neutral' }> = {
      'validated': { text: t('adminReviews.validated'), variant: 'success' },
      'pending': { text: t('adminReviews.pending'), variant: 'warning' },
      'rejected': { text: t('adminReviews.rejected'), variant: 'error' },
    };
    const config = statusMap[status || 'pending'] || statusMap['pending'];
    return <Badge variant={config.variant}>{config.text}</Badge>;
  };

  const getRatingStars = (rating?: number) => {
    if (!rating) return 'N/A';
    return '★'.repeat(rating) + '☆'.repeat(5 - rating) + ` (${rating}/5)`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setRatingFilter('all');
    setProductFilter('all');
    setDateFrom('');
    setDateTo('');
    setCurrentPage(1);
  };

  const hasActiveFilters = searchQuery || statusFilter !== 'all' || ratingFilter !== 'all' || productFilter !== 'all' || dateFrom || dateTo;

  const handleValidateAvis = async (id: number, status: 'validated' | 'rejected') => {
    try {
      setUpdatingStatus(id);
      if (status === 'validated') {
        await apiClient.put(`/cms/testimonials/${id}/validate`);
      } else {
        // Pour rejeter, on utilise deactivate qui met le statut à 'rejected'
        await apiClient.put(`/cms/testimonials/${id}/deactivate`);
      }
      await loadAvis();
    } catch (err: any) {
      console.error('Erreur lors de la mise à jour du statut:', err);
      alert(t('adminReviews.updateStatusError'));
    } finally {
      setUpdatingStatus(null);
    }
  };

  const columns: ResponsiveTableColumn<Avis>[] = [
    {
      key: 'index',
      label: t('adminReviews.no'),
      mobileHidden: true,
      render: (avi, index) => startIndex + index + 1,
    },
    {
      key: 'client',
      label: t('adminReviews.client'),
      render: (avi) => (
        <div>
          <div className="table-cell-name">{avi.customer_name || t('adminReviews.anonymous')}</div>
          {avi.customer_email && (
            <div className="text-xs text-body">{avi.customer_email}</div>
          )}
        </div>
      ),
    },
    {
      key: 'product',
      label: t('adminReviews.product'),
      render: (avi) => (
        <div className="table-cell-text">{avi.product_name || 'N/A'}</div>
      ),
    },
    {
      key: 'rating',
      label: t('adminReviews.ratingCol'),
      render: (avi) => (
        <div className="table-cell-text" style={{ color: '#FF9800', fontWeight: '600' }}>
          {getRatingStars(avi.rating)}
        </div>
      ),
    },
    {
      key: 'comment',
      label: t('adminReviews.comment'),
      mobileHidden: true,
      render: (avi) => (
        <div className="table-cell-text" style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {avi.comment || t('adminReviews.noComment')}
        </div>
      ),
    },
    {
      key: 'date',
      label: t('adminReviews.date'),
      render: (avi) => <div className="table-cell-text">{formatDate(avi.created_at)}</div>,
      mobileHidden: true,
    },
    {
      key: 'status',
      label: t('adminReviews.status'),
      render: (avi) => getStatusBadge(avi.status),
    },
    {
      key: 'actions',
      label: t('adminReviews.actions'),
      mobileHidden: true,
      render: (avi) => (
        <div className="product-actions">
          <button
            className="action-btn action-view"
            title={t('adminReviews.view')}
            aria-label={t('adminReviews.view')}
            onClick={() => setDetailModalOpen(avi)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
            </svg>
          </button>
          {avi.status !== 'validated' && (
            <button
              className="action-btn action-edit"
              title={t('adminReviews.validate')}
              aria-label={t('adminReviews.validate')}
              onClick={() => {
                if (window.confirm(t('adminReviews.validateConfirm'))) {
                  handleValidateAvis(avi.id, 'validated');
                }
              }}
              disabled={updatingStatus === avi.id}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
              </svg>
            </button>
          )}
          {avi.status !== 'rejected' && (
            <button
              className="action-btn action-delete"
              title={t('adminReviews.reject')}
              aria-label={t('adminReviews.reject')}
              onClick={() => {
                if (window.confirm(t('adminReviews.rejectConfirm'))) {
                  handleValidateAvis(avi.id, 'rejected');
                }
              }}
              disabled={updatingStatus === avi.id}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
    <div className="table-card">
              <div className="table-header">
                <h3 className="table-title">{t('adminReviews.title', { count: filteredAvis.length })}</h3>
                <div className="flex gap-2 items-center">
                  {hasActiveFilters && (
                    <button
                      onClick={clearFilters}
                      className="btn btn-secondary btn-sm"
                    >
                      {t('adminReviews.resetFilters')}
                    </button>
                  )}
                </div>
              </div>

              {/* Filtres */}
              <div className="filters-bar">
                <div className="form-group-flex">
                  <label className="form-label">{t('adminReviews.search')}</label>
                  <input
                    type="text"
                    placeholder={t('adminReviews.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="form-group-flex">
                  <label className="form-label">{t('adminReviews.dateFrom')}</label>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="form-group-flex">
                  <label className="form-label">{t('adminReviews.dateTo')}</label>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="form-input"
                  />
                </div>
                <div style={{ minWidth: '150px' }}>
                  <label className="form-label">{t('adminReviews.status')}</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="form-select"
                  >
                    <option value="all">{t('adminReviews.allStatuses')}</option>
                    <option value="validated">{t('adminReviews.validated')}</option>
                    <option value="pending">{t('adminReviews.pending')}</option>
                    <option value="rejected">{t('adminReviews.rejected')}</option>
                  </select>
                </div>
                <div style={{ minWidth: '150px' }}>
                  <label className="form-label">{t('adminReviews.rating')}</label>
                  <select
                    value={ratingFilter}
                    onChange={(e) => setRatingFilter(e.target.value)}
                    className="form-select"
                  >
                    <option value="all">{t('adminReviews.allRatings')}</option>
                    <option value="5">{t('adminReviews.stars5')}</option>
                    <option value="4">{t('adminReviews.stars4')}</option>
                    <option value="3">{t('adminReviews.stars3')}</option>
                    <option value="2">{t('adminReviews.stars2')}</option>
                    <option value="1">{t('adminReviews.stars1')}</option>
                  </select>
                </div>
                <div style={{ minWidth: '180px' }}>
                  <label className="form-label">{t('adminReviews.product')}</label>
                  <select
                    value={productFilter}
                    onChange={(e) => setProductFilter(e.target.value)}
                    className="form-select"
                  >
                    <option value="all">{t('adminReviews.allProducts')}</option>
                    {products.map(product => (
                      <option key={product.id} value={product.id}>
                        {product.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {error ? (
                <Alert type="error" message={error} />
              ) : (
                <ResponsiveTable
                  columns={columns}
                  data={paginatedAvis}
                  loading={loading}
                  loadingMessage={t('adminReviews.loading')}
                  emptyMessage={
                    hasActiveFilters
                      ? t('adminReviews.noFilters')
                      : t('adminReviews.empty')
                  }
                  mobileCardTitle={(avi) => (
                    <div>
                      <div className="table-cell-name mb-1">{avi.customer_name || t('adminReviews.anonymous')}</div>
                      <div className="text-xs text-body">
                        {avi.product_name || 'N/A'} • {formatDate(avi.created_at)}
                      </div>
                    </div>
                  )}
                  mobileCardActions={(avi) => (
                    <div className="product-actions" style={{ display: 'flex', gap: '8px' }}>
                      <button
                        className="action-btn action-view"
                        title={t('adminReviews.view')}
                        aria-label={t('adminReviews.view')}
                        onClick={() => setDetailModalOpen(avi)}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                        </svg>
                      </button>
                      {avi.status !== 'validated' && (
                        <button
                          className="action-btn action-edit"
                          title={t('adminReviews.validate')}
                          aria-label={t('adminReviews.validate')}
                          onClick={() => {
                            if (window.confirm(t('adminReviews.validateConfirm'))) {
                              handleValidateAvis(avi.id, 'validated');
                            }
                          }}
                          disabled={updatingStatus === avi.id}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                          </svg>
                        </button>
                      )}
                      {avi.status !== 'rejected' && (
                        <button
                          className="action-btn action-delete"
                          title={t('adminReviews.reject')}
                          aria-label={t('adminReviews.reject')}
                          onClick={() => {
                            if (window.confirm(t('adminReviews.rejectConfirm'))) {
                              handleValidateAvis(avi.id, 'rejected');
                            }
                          }}
                          disabled={updatingStatus === avi.id}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  )}
                  tableClassName="products-table"
                />
              )}

              {/* Pagination */}
              {!error && totalPages > 1 && (
                <div className="pagination-container" style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '24px',
                  padding: '16px',
                  borderTop: '1px solid #e0e0e0'
                }}>
                  <div className="pagination-info" style={{ fontSize: '14px', color: '#666' }}>
                    {t('adminReviews.displaying', { start: startIndex + 1, end: Math.min(endIndex, filteredAvis.length), total: filteredAvis.length })}
                  </div>
                  <div className="pagination-controls" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      {t('adminReviews.previous')}
                    </button>
                    <div className="pagination-pages" style={{ display: 'flex', gap: '4px' }}>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1)
                        ) {
                          return (
    <>

                            <button
                              key={page}
                              className={`btn ${currentPage === page ? 'btn-primary' : 'btn-secondary'} btn-sm`}
                              onClick={() => setCurrentPage(page)}
                              style={{ minWidth: '36px' }}
                            >
                              {page}
                            </button>
                          
    </>
  );
                        } else if (page === currentPage - 2 || page === currentPage + 2) {
                          return <span key={page} style={{ padding: '0 4px' }}>...</span>;
                        }
                        return null;
                      })}
                    </div>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                    >
                      {t('adminReviews.next')}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Modal de détail de l'avis */}
            {detailModalOpen && (
              <div 
                className="modal-overlay" 
                onClick={() => setDetailModalOpen(null)}
                role="dialog"
                aria-modal="true"
                aria-labelledby="detail-modal-title"
              >
                <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', width: '90%' }}>
                  <div className="modal-header">
                    <h3 id="detail-modal-title" className="modal-title">
                      {t('adminReviews.detailTitle', { id: detailModalOpen.id })}
                    </h3>
                    <button 
                      className="modal-close"
                      onClick={() => setDetailModalOpen(null)}
                      aria-label={t('adminReviews.close')}
                    >
                      <XMarkIcon className="btn-icon" />
                    </button>
                  </div>
                  <div className="modal-body">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                      {/* Informations client */}
                      <div>
                        <h4 style={{ marginBottom: '12px', fontSize: '16px', fontWeight: '600', color: '#333' }}>
                          {t('adminReviews.clientInfo')}
                        </h4>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                          <div>
                            <strong>{t('adminReviews.name')}</strong> {detailModalOpen.customer_name || t('adminReviews.anonymous')}
                          </div>
                          {detailModalOpen.customer_email && (
                            <div>
                              <strong>{t('adminReviews.email')}</strong> {detailModalOpen.customer_email}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Informations produit */}
                      <div>
                        <h4 style={{ marginBottom: '12px', fontSize: '16px', fontWeight: '600', color: '#333' }}>
                          {t('adminReviews.productConcerned')}
                        </h4>
                        <div>
                          <strong>{t('adminReviews.productLabel')}</strong> {detailModalOpen.product_name || 'N/A'}
                          {detailModalOpen.product_id && (
                            <span style={{ color: '#666', marginLeft: '8px' }}>(ID: {detailModalOpen.product_id})</span>
                          )}
                        </div>
                      </div>

                      {/* Note */}
                      <div>
                        <h4 style={{ marginBottom: '12px', fontSize: '16px', fontWeight: '600', color: '#333' }}>
                          {t('adminReviews.ratingCol')}
                        </h4>
                        <div style={{ fontSize: '24px', color: '#FF9800', fontWeight: '600' }}>
                          {detailModalOpen.rating ? (
                            <>
                              {'★'.repeat(detailModalOpen.rating)}{'☆'.repeat(5 - detailModalOpen.rating)}
                              <span style={{ fontSize: '16px', marginLeft: '8px', color: '#333' }}>
                                ({detailModalOpen.rating}/5)
                              </span>
                            </>
                          ) : (
                            'N/A'
                          )}
                        </div>
                      </div>

                      {/* Commentaire */}
                      <div>
                        <h4 style={{ marginBottom: '12px', fontSize: '16px', fontWeight: '600', color: '#333' }}>
                          {t('adminReviews.commentSection')}
                        </h4>
                        <div style={{ 
                          padding: '16px', 
                          background: '#f9f9f9', 
                          borderRadius: '8px',
                          border: '1px solid #e0e0e0',
                          minHeight: '100px',
                          whiteSpace: 'pre-wrap',
                          wordWrap: 'break-word'
                        }}>
                          {detailModalOpen.comment || t('adminReviews.noComment')}
                        </div>
                      </div>

                      {/* Statut et dates */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                        <div>
                          <strong>{t('adminReviews.statusLabel')}</strong>
                          <div style={{ marginTop: '8px' }}>
                            {getStatusBadge(detailModalOpen.status)}
                          </div>
                        </div>
                        <div>
                          <strong>{t('adminReviews.featured')}</strong>
                          <div style={{ marginTop: '8px' }}>
                            {detailModalOpen.featured_on_home ? (
                              <Badge variant="success">{t('adminAddProduct.yes')}</Badge>
                            ) : (
                              <Badge variant="neutral">{t('adminAddProduct.no')}</Badge>
                            )}
                          </div>
                        </div>
                        <div>
                          <strong>{t('adminReviews.createdAt')}</strong>
                          <div style={{ marginTop: '4px', color: '#666' }}>
                            {formatDate(detailModalOpen.created_at)}
                          </div>
                        </div>
                        {detailModalOpen.updated_at && detailModalOpen.updated_at !== detailModalOpen.created_at && (
                          <div>
                            <strong>{t('adminReviews.lastModified')}</strong>
                            <div style={{ marginTop: '4px', color: '#666' }}>
                              {formatDate(detailModalOpen.updated_at)}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setDetailModalOpen(null)}
                    >
                      {t('adminReviews.close')}
                    </button>
                    {detailModalOpen.status !== 'validated' && (
                      <button
                        className="btn btn-primary"
                        onClick={() => {
                          handleValidateAvis(detailModalOpen.id, 'validated');
                          setDetailModalOpen(null);
                        }}
                        disabled={updatingStatus === detailModalOpen.id}
                      >
                        {updatingStatus === detailModalOpen.id ? t('adminReviews.validating') : t('adminReviews.validateReview')}
                      </button>
                    )}
                    {detailModalOpen.status !== 'rejected' && (
                      <button
                        className="btn btn-secondary"
                        onClick={() => {
                          if (window.confirm('Êtes-vous sûr de vouloir rejeter cet avis ?')) {
                            handleValidateAvis(detailModalOpen.id, 'rejected');
                            setDetailModalOpen(null);
                          }
                        }}
                        disabled={updatingStatus === detailModalOpen.id}
                        style={{ backgroundColor: '#f44336', color: 'white', borderColor: '#f44336' }}
                      >
                        {updatingStatus === detailModalOpen.id ? t('adminReviews.rejecting') : t('adminReviews.rejectReview')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
    </>
  );
};
